// src/pages/PayThanksPage.tsx
// Post-payment success interstitial. PayPage redirects here after Razorpay
// confirms; this page fires the Google Ads purchase conversion exactly once,
// then auto-forwards to /codemate. It is also the stable "page after purchase"
// URL for Google Ads page-view conversion goals: https://cubemate.ai/pay/thanks

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Paste the conversion label from Google Ads once the "Purchase" conversion
// action is created (Goals → Conversions → the action → Tag setup → event
// snippet; the label is the part after the slash in send_to). While empty,
// the Google Ads conversion ping is skipped — the GA4 purchase event below
// still fires.
const GOOGLE_ADS_ID = 'AW-18013772297';
const GOOGLE_ADS_PURCHASE_LABEL = '';

const FORWARD_DELAY_MS = 2200;

interface PurchaseInfo {
  tier?: string;
  amount?: number;
  paymentId?: string;
}

const PayThanksPage: React.FC = () => {
  const navigate = useNavigate();
  const fired = useRef(false);

  useEffect(() => {
    if (!fired.current) {
      fired.current = true;
      try {
        const raw = sessionStorage.getItem('cubemate_purchase');
        if (raw) {
          // One-shot: remove before firing so a refresh can't double-count.
          sessionStorage.removeItem('cubemate_purchase');
          const info: PurchaseInfo = JSON.parse(raw);
          if (typeof window.gtag === 'function') {
            if (GOOGLE_ADS_PURCHASE_LABEL) {
              window.gtag('event', 'conversion', {
                send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
                value: info.amount || 0,
                currency: 'INR',
                transaction_id: info.paymentId || '',
              });
            }
            window.gtag('event', 'purchase', {
              value: info.amount || 0,
              currency: 'INR',
              transaction_id: info.paymentId || '',
              items: [{ item_name: info.tier || 'unknown' }],
            });
          }
        }
      } catch { /* analytics must never block the redirect */ }
    }
    const t = setTimeout(() => navigate('/codemate', { replace: true }), FORWARD_DELAY_MS);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-6">
      <div className="max-w-md w-full p-8 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="mt-5 text-xl font-semibold text-gray-900 dark:text-white">Payment successful 🎉</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Your plan is active. Taking you to your workspace…
        </p>
        <div className="mt-6 flex justify-center">
          <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    </div>
  );
};

export default PayThanksPage;
